import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { LayoutElementV2 } from '@/components/inspector-fields/LayoutElement/LayoutElementV2';
import NodePositionSettingsV2 from '@/components/dropdowns/Position/NodePositionSettingsV2';
import { NodeZIndexSettingsV2 } from '@/components/inspector-fields/SliderField/NodeZIndexSettingsV2';
import { InspectorHeader } from '../InspectorHeader/InspectorHeader';
import IconMoreHorizontalSquare from '@/assets/icons/icon-more-horizontal-square.svg?react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { StyleField } from '@/components/inspector-fields/StyleField/StyleField';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import { Event } from '@/components/dropdowns/Event/Event';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { SelectItem } from '@/components/ui/select';
import NodeStrokeSettings from '@/components/inspector-fields/SliderField/NodeStrokeSettings';
import { useNode, useEditor } from '@craftjs/core';
import type { ButtonProps } from '@builder4/shared/components/Button';
import type { Button } from '@builder4/shared/types/styles';
import type { Color, Gradient, RGBA } from '@builder4/shared/types/layout';
import {
  BUTTON_VARIANT_TO_KEY,
  type ButtonVariant,
  type ButtonSize,
  type ButtonType,
} from '@builder4/shared/styles/constants';
import { useAppSelector } from '@/store/hooks';
import { selectActiveGlobalStyle } from '@/store/globalStyles/selectors';
import NodeSpaceSettings from '@/components/inspector-fields/SliderField/NodeSpaceSettings';
import NodeTextSizeSettings from '@/components/inspector-fields/SliderField/NodeTextSizeSettings';
import NodeFontFamilySettings from '@/components/dropdowns/SelectField/NodeFontFamilySettings';
import type { ProcessedFont } from '@builder4/shared/types/fonts';
import { useFonts } from '@/context/FontProvider';
import NodeFontWeightSettings from '@/components/dropdowns/SelectField/NodeFontWeightSettings';
import { TextField } from '@/components/inspector-fields/TextField/TextField';
import { SizeField } from '@/components/inspector-fields/SizeField/SizeField';
import NodeVisibilitySettings from '@/components/inspector-fields/Visibility/NodeVisibilitySettings';
import IconUnlink from '@/assets/icons/icon-unlink-01.svg?react';
import IconLink from '@/assets/icons/icon-link-01.svg?react';
import { getButtonHeight, getButtonTypeFromRadius } from '@/utils/helperFunc';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { getDetachablePropsForNode } from '@builder4/shared/utils/constants';
import { checkIsGradient, isColorReference } from '@builder4/shared/utils/styleHelpers';
import { Switch } from '@/components/inspector-fields/Switch/Switch';
import { NodeDimensionSettings } from '@/components/inspector-fields/DimensionsFields/NodeDimensionSettings';
import { useCraftHelper } from '@/hooks/useCraftHelper';
import NodeColorField from '@/components/inspector-fields/ColorField/NodeColorField';
import NodeAnimationSettings from '@/components/inspector-fields/AnimationSettings/NodeAnimationSettings';
import NodeShadowField from '@/components/inspector-fields/ShadowField/NodeShadowField';
import { ContentAlignmentHorizontal } from '@/components/inspector-fields/ContentAlignmentHorizontal/ContentAlignmentHorizontal';
import { ContentAlignmentVertical } from '@/components/inspector-fields/ContentAlignmentVertical/ContentAlignmentVertical';
import type { ContainerProps } from '@builder4/shared/components/Container';
import { getBreakpointProp } from '@builder4/shared/utils/breakpointUtils';
import { selectBreakpoint } from '@/store/system/selectors';

interface InspectorButtonProps {
  className: string;
  text: string;
}

export const InspectorButton: React.FC<InspectorButtonProps> = ({ className, text = 'Button' }) => {
  const {
    id,
    actions: { setProp },
    props,
    appliedGlobalStyle,
    text: buttonText,
    buttonSize,
    buttonVariant,
    fontSize,
    borderWidth,
    displayName,
    fillWidth,
    parentId,
  } = useNode((node) => ({
    props: node.data.props as ButtonProps,
    appliedGlobalStyle: node.data.props.appliedGlobalStyle,
    text: node.data.props.text,
    buttonSize: node.data.props.buttonSize,
    buttonVariant: node.data.props.buttonVariant,
    node: node,
    fontSize: node.data.props.fontSize,
    borderWidth: node.data.props.borderWidth,
    displayName: node.data.displayName,
    fillWidth: node.data.props.fillWidth,
    parentId: node.data.parent,
  }));
  const { getBPProp, setBPProp } = useBreakpointAwareProp(displayName);
  const position = getBPProp(props, 'position');
  const borderRadius = getBPProp(props, 'borderRadius') || [0, 0, 0, 0];
  const padding = getBPProp(props, 'padding') ?? [0, 0, 0, 0];
  const [buttonType, setButtonType] = useState<ButtonType | undefined>('square');
  const { fillButtonWidth } = useCraftHelper();
  const activeGlobalStyle = useAppSelector(selectActiveGlobalStyle);
  const breakpoint = useAppSelector(selectBreakpoint);

  // Read parent container alignment props reactively
  const {
    actions: editorActions,
    parentProps: parentContainerProps,
    parentDisplayName,
  } = useEditor((state) => {
    const pNode = parentId ? state.nodes[parentId] : null;
    return {
      parentProps: pNode?.data.props as Partial<ContainerProps> | undefined,
      parentDisplayName: pNode?.data.displayName,
    };
  });
  const parentFlexDirection = parentContainerProps
    ? (getBreakpointProp(parentContainerProps, 'flexDirection', breakpoint) ?? 'column')
    : 'column';
  const parentAlignItems = parentContainerProps
    ? (getBreakpointProp(parentContainerProps, 'alignItems', breakpoint) ?? 'center')
    : 'center';
  const parentJustifyContent = parentContainerProps
    ? (getBreakpointProp(parentContainerProps, 'justifyContent', breakpoint) ?? 'center')
    : 'center';

  const { fonts } = useFonts();
  const [selectedFontFamily, setSelectedFontFamily] = useState<ProcessedFont>();

  const handleOnChangeButtonType = (value: ButtonType) => {
    setButtonType(value);
    setProp((props: ButtonProps) => {
      const buttonHeight = getButtonHeight(
        props.padding[0],
        props.padding[2],
        parseFloat(props.fontSize || '0'),
        props.borderWidth,
      );

      let buttonRadius = 0;
      if (value === 'round') {
        buttonRadius = Math.round(buttonHeight * 0.2);
      } else if (value === 'pill') {
        buttonRadius = Math.round(buttonHeight * 0.5);
      }
      setBPProp(props, 'borderRadius', [buttonRadius, buttonRadius, buttonRadius, buttonRadius]);
    }, 500);
  };

  useEffect(() => {
    if (props.fontFamily !== undefined && fonts.length > 0) {
      const selected = fonts.find((font) => font.family === props.fontFamily);
      setSelectedFontFamily(selected);
    }
  }, [props.fontFamily, fonts]);

  // Calculate Button Type when relevant properties change
  useEffect(() => {
    const allCornersEqual =
      borderRadius[0] === borderRadius[1] &&
      borderRadius[1] === borderRadius[2] &&
      borderRadius[2] === borderRadius[3];

    if (allCornersEqual) {
      const buttonHeight = getButtonHeight(
        padding[0],
        padding[2],
        parseFloat(fontSize || '0'),
        borderWidth,
      );
      const newButtonType = getButtonTypeFromRadius(borderRadius[0], buttonHeight);
      if (buttonType !== newButtonType) {
        setButtonType(newButtonType);
      }
    }
  }, [borderRadius, padding, fontSize, borderWidth]);

  const handleOnToggleGlobalStyle = () => {
    setProp((props: ButtonProps) => {
      if (props.appliedGlobalStyle) {
        if (buttonVariant && buttonSize) {
          const currentStyles = activeGlobalStyle;
          const buttonKey = BUTTON_VARIANT_TO_KEY[props.buttonVariant];

          if (!buttonKey) {
            console.warn('Invalid variant:', buttonVariant);
            props.appliedGlobalStyle = false;
            return;
          }

          const buttonConfig = currentStyles.buttons[buttonKey] as Button;
          const sizeConfig = buttonConfig[props.buttonSize];

          const backgroundColor = buttonConfig.backgroundColor;
          const isGradient = checkIsGradient(backgroundColor as Color);
          const isRGBA = !isGradient && !isColorReference(backgroundColor as Color);

          const fontColor = buttonConfig.fontColor;
          const isGradientFontColor = checkIsGradient(fontColor as Color);
          const isRGBAFontColor = !isGradientFontColor && !isColorReference(fontColor as Color);
          // Create object with all props from global styles
          const propsData: Partial<ButtonProps> = {
            appliedGlobalStyle: false,
            fontFamily: buttonConfig.fontFamily,
            fontWeight: buttonConfig.fontWeight,
            fontSize: `${sizeConfig.fontSize}px`,
            letterSpacing: buttonConfig.letterSpacing,
            borderWidth: buttonConfig.borderWidth,
            borderColor: buttonConfig.borderColor as RGBA,
            borderRadius: sizeConfig.borderRadius,
            padding: sizeConfig.padding,
            margin: sizeConfig.margin,

            color: buttonConfig.fontColor,
            textSolid: isRGBAFontColor ? (buttonConfig.fontColor as RGBA) : undefined,
            textGradient: isGradientFontColor ? (buttonConfig.fontColor as Gradient) : undefined,

            backgroundColor: buttonConfig.backgroundColor ?? undefined,
            bgSolid: isRGBA ? (buttonConfig.backgroundColor as RGBA) : undefined,
            bgGradient: isGradient ? (backgroundColor as Gradient) : undefined,
            // these hover colors are not defined in global styles, so we set them to undefined
            hoverBorderColor: undefined,
            hoverBackgroundColor: undefined,
            hoverTextColor: undefined,
          };

          const detachableProps = getDetachablePropsForNode(displayName);
          const detachablePropsSet = new Set(detachableProps);

          Object.assign(props, propsData);

          // Clear breakpoint-specific overrides for detachable props
          Object.keys(propsData).forEach((key) => {
            if (detachablePropsSet.has(key)) {
              if (props.tablet) {
                delete (props.tablet as Record<string, unknown>)[key];
              }
              if (props.mobile) {
                delete (props.mobile as Record<string, unknown>)[key];
              }
            }
          });
        } else {
          console.warn('Cannot detach: missing buttonVariant or buttonSize', {
            buttonVariant,
            buttonSize,
          });
          props.appliedGlobalStyle = false; // Still detach, but without global style values
        }
      } else {
        // Attaching: Just switch the flag, Button component will handle the rest
        props.appliedGlobalStyle = true;
      }
    }, 500);
  };
  const handleOnChangeButtonText = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProp((props: ButtonProps) => {
      setBPProp(props, 'text', e.target.value);
    }, 500);
  };
  const horizontalMapping: Record<string, string> = {
    left: 'flex-start',
    right: 'flex-end',
    center: 'center',
    'flex-start': 'left',
    'flex-end': 'right',
  };
  const verticalMapping: Record<string, string> = {
    top: 'flex-start',
    bottom: 'flex-end',
    center: 'center',
    'flex-start': 'top',
    'flex-end': 'bottom',
  };

  const currentHAlign =
    horizontalMapping[parentFlexDirection === 'row' ? parentJustifyContent : parentAlignItems] ??
    'center';
  const currentVAlign =
    verticalMapping[parentFlexDirection === 'row' ? parentAlignItems : parentJustifyContent] ??
    'center';

  const handleHAlignmentChange = (alignment: string) => {
    if (!parentId) return;
    const flexValue = horizontalMapping[alignment];
    editorActions.setProp(parentId, (props: Partial<ContainerProps>) => {
      if (parentFlexDirection === 'row') {
        setBPProp(props, 'justifyContent' as keyof ContainerProps, flexValue, parentDisplayName);
      } else {
        setBPProp(props, 'alignItems' as keyof ContainerProps, flexValue, parentDisplayName);
      }
    });
  };
  const handleVAlignmentChange = (alignment: string) => {
    if (!parentId) return;
    const flexValue = verticalMapping[alignment];
    editorActions.setProp(parentId, (props: Partial<ContainerProps>) => {
      if (parentFlexDirection === 'row') {
        setBPProp(props, 'alignItems' as keyof ContainerProps, flexValue, parentDisplayName);
      } else {
        setBPProp(props, 'justifyContent' as keyof ContainerProps, flexValue, parentDisplayName);
      }
    });
  };
  const onHandleButtonVariantChange = (value: ButtonVariant) => {
    setProp((props: ButtonProps) => {
      setBPProp(props, 'buttonVariant', value);
    }, 500);
  };
  const onHandleButtonSizeClick = (value: ButtonSize) => {
    setProp((props: ButtonProps) => {
      setBPProp(props, 'buttonSize', value);
    }, 500);
  };

  const GlobalStyleToggleComponent = () => {
    return (
      <>
        <SecondaryButton
          onClick={handleOnToggleGlobalStyle}
          icon={appliedGlobalStyle ? IconUnlink : IconLink}
          size="md"
          state="default"
          text={appliedGlobalStyle ? 'Detach from Style' : 'Apply Style'}
          data-qa-selector="inspector-button-toggle-style"
        />

        <p className="text-button-sm font-medium text-neutral-400">
          {appliedGlobalStyle
            ? 'Unlink this element from styles to customize it individually.'
            : 'Link element to the latest applied Style.'}
        </p>
      </>
    );
  };

  const handleFullWidthChange = (checked: boolean) => {
    setProp((props: ButtonProps) => {
      setBPProp(props, 'fillWidth', checked);
      setBPProp(props, 'fillSpace', checked ? 'yes' : 'no');
    });
    if (checked) {
      fillButtonWidth(parentId as string, id);
    } else {
      setProp((props: ButtonProps) => {
        setBPProp(props, 'width', 'auto');
        setBPProp(props, 'fillSpace', 'no');
        setBPProp(props, 'flexBasis', 'auto');
      });
    }
  };

  return (
    <div
      className={clsx(
        'inspector bg-neutral-0 relative z-10 inline-flex h-[calc(100vh-52px)] max-w-[240px] flex-col border-l border-neutral-100',
        className,
      )}
    >
      <InspectorHeader
        className="max-w-[240px] text-neutral-900"
        icon={IconMoreHorizontalSquare}
        text={text}
        showStyle={true}
        applied={appliedGlobalStyle}
        onToggleGlobalStyle={handleOnToggleGlobalStyle}
      />

      <ScrollArea className="h-[calc(100vh-104px)] overflow-hidden">
        <div className="flex flex-col gap-3 p-3">
          {appliedGlobalStyle ? (
            <>
              <p className="pb-1 text-xs font-semibold text-neutral-900">Layout</p>
              <NodePositionSettingsV2 data-qa-selector="inspector-button-position" />
              <NodeVisibilitySettings data-qa-selector="inspector-button-visibility" />
              <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
              <p className="pb-1 text-xs font-semibold text-neutral-900">Dimensions</p>

              <NodeDimensionSettings
                className="w-full max-w-[216px]"
                data-qa-selector="inspector-button-dimensions"
              />
              <Switch
                checked={fillWidth}
                onCheckedChange={handleFullWidthChange}
                label="Fill Width"
                data-qa-selector="inspector-button-fill-width"
              />
              <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
              <p className="pb-1 text-xs font-semibold text-neutral-900">Button</p>

              <SelectField
                defaultValue={buttonVariant ?? 'primary'}
                size={'large'}
                text="Type"
                dataTestId="variant-selection"
                onValueChange={(value) => onHandleButtonVariantChange(value as ButtonVariant)}
                data-qa-selector="inspector-button-variant"
              >
                <SelectItem value="primary">Primary</SelectItem>
                <SelectItem value="secondary">Secondary</SelectItem>
                <SelectItem value="accent">Accent</SelectItem>
              </SelectField>
              <TextField
                className="max-w-[216px]"
                inputValue={buttonText}
                onChange={handleOnChangeButtonText}
                placeholderText=""
                state="default"
                text="Text"
                type="text"
                data-qa-selector="inspector-button-text"
              />
              <SizeField
                size={buttonSize}
                onClick={onHandleButtonSizeClick}
                text="Size"
                data-qa-selector="inspector-button-size"
              />
              <ContentAlignmentHorizontal
                onClick={handleHAlignmentChange}
                align={currentHAlign}
                text="H Alignment"
                data-qa-selector="inspector-button-h-align"
              />
              <ContentAlignmentVertical
                onClick={handleVAlignmentChange}
                align={currentVAlign}
                text="V Alignment"
                data-qa-selector="inspector-button-v-align"
              />
              <GlobalStyleToggleComponent />
              {position !== 'relative' && (
                <NodeZIndexSettingsV2 data-qa-selector="inspector-button-zindex" />
              )}
            </>
          ) : (
            <>
              <LayoutElementV2
                className=""
                onClick={() => {}}
                state="default"
                data-qa-selector="inspector-button-layout-element"
              />
              <Switch
                checked={fillWidth}
                onCheckedChange={handleFullWidthChange}
                label="Fill Width"
                data-qa-selector="inspector-button-fill-width-custom"
              />
              <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

              <p className="pb-1 text-xs font-semibold text-neutral-900">Button</p>
              <TextField
                className="max-w-[216px]"
                inputValue={buttonText}
                onChange={handleOnChangeButtonText}
                placeholderText=""
                state="default"
                text="Text"
                type="text"
                data-qa-selector="inspector-button-text-custom"
              />
              <StyleField
                onClick={handleOnChangeButtonType}
                style={buttonType}
                text="Style"
                data-qa-selector="inspector-button-style"
              />
              <Switch
                checked={fillWidth}
                onCheckedChange={handleFullWidthChange}
                label="Fill Width"
                data-qa-selector="inspector-button-fill-width-duplicate"
              />

              <NodeColorField
                propertyName="backgroundColor"
                solidPropertyName="bgSolid"
                gradientPropertyName="bgGradient"
                gradientEnabled
                label="Fill Color"
                data-qa-selector="inspector-button-fill-color"
              />

              <NodeColorField
                propertyName="hoverBackgroundColor"
                solidPropertyName="hoverBackgroundSolid"
                gradientPropertyName="hoverBackgroundGradient"
                gradientEnabled
                label="Fill Hover"
                data-qa-selector="inspector-button-fill-hover"
              />

              <NodeColorField
                propertyName="borderColor"
                ensureStrokeVisibleProp="borderWidth"
                label="Stroke Color"
                data-qa-selector="inspector-button-stroke-color"
              />

              <NodeColorField
                propertyName="hoverBorderColor"
                ensureStrokeVisibleProp="borderWidth"
                label="Stroke Hover"
                data-qa-selector="inspector-button-stroke-hover"
              />

              <NodeStrokeSettings data-qa-selector="inspector-button-stroke" />

              <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

              <p className="pb-1 text-xs font-semibold text-neutral-900">Text</p>
              <NodeFontFamilySettings data-qa-selector="inspector-button-font-family" />
              <NodeFontWeightSettings
                fontFamily={selectedFontFamily}
                data-qa-selector="inspector-button-font-weight"
              />

              <NodeColorField
                propertyName="color"
                solidPropertyName="textSolid"
                gradientPropertyName="textGradient"
                gradientEnabled
                label="Text Color"
                data-qa-selector="inspector-button-text-color"
              />

              <NodeColorField
                propertyName="hoverTextColor"
                solidPropertyName="hoverTextSolid"
                gradientPropertyName="hoverTextGradient"
                gradientEnabled
                label="Text Hover"
                data-qa-selector="inspector-button-text-hover"
              />

              <NodeTextSizeSettings data-qa-selector="inspector-button-text-size" />
              <NodeSpaceSettings data-qa-selector="inspector-button-space" />
              <ContentAlignmentHorizontal
                onClick={handleHAlignmentChange}
                align={currentHAlign}
                text="H Alignment"
                data-qa-selector="inspector-button-h-align-custom"
              />
              <ContentAlignmentVertical
                onClick={handleVAlignmentChange}
                align={currentVAlign}
                text="V Alignment"
                data-qa-selector="inspector-button-v-align-custom"
              />
              <GlobalStyleToggleComponent />
            </>
          )}

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
          <p className="pb-1 text-xs font-semibold text-neutral-900">Appearance</p>
          <NodeShadowField data-qa-selector="inspector-button-shadow" />

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
          <p className="pb-1 text-xs font-semibold text-neutral-900">Interaction</p>
          <Event className="" text="Event" data-qa-selector="inspector-button-event" />
          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
          <NodeAnimationSettings />
        </div>
      </ScrollArea>
    </div>
  );
};
